// Company profiles: what a company does, the market it works in, who it sells to, what it is strong at, the
// capabilities it lacks and the team's angle on it; plus what the team itself offers, which shows the gaps the team
// can fill. Shared by the server and the web app.

export const PROFILE_LIMITS = {
  /** "What they do", one line. */
  summary: 200,
  market: 60,
  /** "Sells to". */
  clients: 200,
  /** "Our angle". */
  angle: 1000,
  /** One capability name. */
  capability: 40,
  /** Capabilities per list (strengths or gaps) on one company. */
  capabilities: 20,
  /** Capabilities the team offers. */
  offerings: 40,
} as const;

/** A capability in use, with how many live companies list it as a strength or a gap. */
export interface CapabilityCount {
  name: string;
  strengths: number;
  lacks: number;
  /** The team offers it. */
  offered: boolean;
}

export interface MarketCount {
  name: string;
  companies: number;
}

/** The team's profile vocabulary: what it offers, every capability and every market in use. */
export interface ProfileTerms {
  /** In the team's order. */
  offerings: string[];
  /** Most used first. */
  capabilities: CapabilityCount[];
  /** Most used first. */
  markets: MarketCount[];
}

export const PROFILE_TERMS_KEY = ['profile-terms'] as const;

/** One name as stored: single spaces, no space at either end. */
export function normaliseTerm(raw: string): string {
  return raw.replace(/\s+/g, ' ').trim();
}

/** Names in order, each once: later ones that differ only in case or spacing are dropped, as are empty ones. */
export function normaliseTerms(list: readonly string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of list) {
    const name = normaliseTerm(raw);
    const key = name.toLowerCase();
    if (!name || seen.has(key)) continue;
    seen.add(key);
    out.push(name);
  }
  return out;
}

export function sameTerm(a: string, b: string): boolean {
  return normaliseTerm(a).toLowerCase() === normaliseTerm(b).toLowerCase();
}

export function hasTerm(list: readonly string[], name: string): boolean {
  return list.some((x) => sameTerm(x, name));
}

/** The company's gaps the team offers, in the company's order. */
export function gapsYouFill(lacks: readonly string[], offerings: readonly string[]): string[] {
  const offered = new Set(offerings.map((o) => normaliseTerm(o).toLowerCase()));
  return lacks.filter((l) => offered.has(normaliseTerm(l).toLowerCase()));
}

/**
 * Strengths and gaps after a change, so a capability is never both. A capability that turns up in both stays in
 * the list named by `prefer`, the one that just changed; with both changed at once, strengths win.
 */
export function separateCapabilities(strengths: readonly string[], lacks: readonly string[], prefer: 'strengths' | 'lacks'): { strengths: string[]; lacks: string[] } {
  const s = normaliseTerms(strengths);
  const l = normaliseTerms(lacks);
  if (prefer === 'lacks') return { strengths: s.filter((x) => !hasTerm(l, x)), lacks: l };
  return { strengths: s, lacks: l.filter((x) => !hasTerm(s, x)) };
}

/** Each name in the spelling the team already uses for it, when it uses one. `known` maps lower-cased names to spellings. */
export function spellTerms(list: readonly string[], known: ReadonlyMap<string, string>): string[] {
  return normaliseTerms(list.map((n) => known.get(normaliseTerm(n).toLowerCase()) ?? normaliseTerm(n)));
}

/** "Backend", "Backend and DevOps", "Backend, DevOps and 3D". */
export function termList(list: readonly string[]): string {
  if (list.length <= 1) return list[0] ?? '';
  return `${list.slice(0, -1).join(', ')} and ${list[list.length - 1]}`;
}
